/**
 * Cores semânticas de status de OS — deliberadamente FORA da marca.
 *
 * O briefing (`18 / Estratégia cromática`) é explícito: a cor institucional não
 * pode disputar espaço com código semântico dentro do produto. Por isso a família
 * teal de `palette.ts` é exclusiva de interação e nenhum status abaixo a usa.
 *
 * Cada status tem três valores:
 *   `base`  — o hex do status; usado como ponto/barra sobre fundo ESCURO
 *   `onLight` — variante escurecida, para TEXTO sobre o badge claro (AA em fundo claro)
 *   `bgLight` / `bgDark` — o preenchimento do badge em cada tema
 */

/** Matizes em uso, para conferir separação ao adicionar um status novo. */
const HUES = {
  analysis: 217,
  execution: 25,
  waitingPart: 45,
  outsourced: 258,
  completed: 142,
  waitingPayment: 330,
  delivered: 160,
  /** accent da marca — nenhum status pode chegar perto */
  brandAccent: 177,
} as const;

export type OSStatusKey =
  | 'in_analysis'
  | 'in_execution'
  | 'waiting_part'
  | 'outsourced'
  | 'completed'
  | 'waiting_payment'
  | 'cancelled'
  | 'delivered';

export interface StatusColor {
  readonly base: string;
  readonly onLight: string;
  readonly bgLight: string;
  readonly bgDark: string;
}

export const OS_STATUS_COLORS: Record<OSStatusKey, StatusColor> = {
  in_analysis: { base: '#3B82F6', onLight: '#1D4ED8', bgLight: '#DBEAFE', bgDark: '#151E2E' },
  in_execution: { base: '#F97316', onLight: '#C2410C', bgLight: '#FFEDD5', bgDark: '#2E1E12' },
  waiting_part: { base: '#EAB308', onLight: '#A16207', bgLight: '#FEF9C3', bgDark: '#2E2612' },
  outsourced: { base: '#8B5CF6', onLight: '#6D28D9', bgLight: '#EDE9FE', bgDark: '#1E152E' },
  completed: { base: '#22C55E', onLight: '#15803D', bgLight: '#DCFCE7', bgDark: '#162E1E' },

  /**
   * MUDOU no rebrand. Era ciano `#06B6D4` (H=189°), a 12° da Turquesa Must
   * (H=177°) — com a turquesa virando accent interativo, "aguardando pagamento"
   * passava a parecer um botão. Rosa H=330° fica a 72° do violeta de
   * `outsourced` e a 34° do vermelho de perigo.
   */
  waiting_payment: { base: '#EC4899', onLight: '#BE185D', bgLight: '#FCE7F3', bgDark: '#2E1222' },

  cancelled: { base: '#6B7280', onLight: '#4B5563', bgLight: '#F3F4F6', bgDark: '#1E1E1E' },

  /**
   * TODO (produto, não rebrand): `delivered` (#10B981, H=160°) e `completed`
   * (#22C55E, H=142°) são quase indistinguíveis num badge pequeno. É um problema
   * pré-existente e mexer nele muda a leitura do fluxo de OS — decidir junto com
   * a UX antes de trocar.
   */
  delivered: { base: '#10B981', onLight: '#047857', bgLight: '#D1FAE5', bgDark: '#122E22' },
};

/** Cores de intenção — sucesso, alerta, perigo, informação. Também fora da marca. */
export const SEMANTIC = {
  success: { base: '#22C55E', onLight: '#15803D', bgLight: '#DCFCE7', bgDark: '#162E1E' },
  warning: { base: '#FBBF24', onLight: '#B45309', bgLight: '#FEF3C7', bgDark: '#2E2612' },
  danger: { base: '#EF4444', onLight: '#B91C1C', bgLight: '#FEE2E2', bgDark: '#2E1515' },
  info: { base: '#3B82F6', onLight: '#1D4ED8', bgLight: '#DBEAFE', bgDark: '#151E2E' },
} as const;

export { HUES as STATUS_HUES };
